/**
 * practice-flow.ts — 「出練習卷」閉環 (LEARNING_SYSTEM_DESIGN_v2 §0 #2/#3/#5/#7, §1).
 *
 * 串起 v2 vertical slice (`math.div.remainder`) 的整條學習迴圈,純學習側、無戰鬥:
 *
 *   1. 出卷:`advancePracticePaperCount` 推進卷次時鐘 → `composePracticePaper`
 *      動態組卷 (Leitner-aware) → 用「考卷排版」(沿用 exam-bank/paper-view 的
 *      版面語彙:卷頭 + 連續題號 + 作答格,★挑戰題標「寫不出來沒關係」可跳過)
 *      顯示整張卷 + 🖨️列印 (小孩印出用筆寫)。
 *   2. 家長批改 (非小孩 app quiz):寫完後一個「對答案」畫面,逐題顯示正解,
 *      家長每題點【對 / 粗心 / 不會】三個鈕 (§0 #5:判定交家長一鍵,不靠 LLM 猜)。
 *   3. 升降盒 + 入帳:每題依家長標記呼 `recordAttemptV2` (對→correct、
 *      粗心→careless、不會→wrong) 更新 Leitner (卷次驅動);批改完該科
 *      `addFactionXP(progress, factionForSubject('math'), xp)` (xp 依答對數)
 *      → `saveProgress` (合法「學習=戰力」,§0 #7;絕不彈題進戰鬥)。
 *   4. 即時回饋 (§0 #7):批改完即時顯示「這次對幾題 / 除法餘數 熟練度盒 X→Y /
 *      修煉值 +N / 連續天數」,純學習側。
 *
 * 學習介面要專業 (boss 2026-05-23):純文字、極簡,無音效、無 mascot、無彩光。
 * 學習/遊戲分離鐵則:純學習側,只動 factionXP (合法學習入帳),絕不碰
 * commanderXp / 戰鬥數值,絕不在此流程彈出戰鬥。
 */

import { loadProgress, saveProgress, addFactionXP } from '../../shared/progress';
import { factionForSubject, FACTIONS } from '../../shared/factions';
import {
  advancePracticePaperCount,
  recordAttemptV2,
  findOrCreateKP,
} from '../../shared/student-kp';
import { composePracticePaper } from './smart-composer';
import type { SmartPaperQuestion } from './smart-composer';
import { mountEscapeButton } from '../escape-button';
import { ensureAcademicTheme } from '../ui/theme';
import { academicButton } from '../ui/components';
import type { AcademicButtonVariant } from '../ui/components';
import type { PlayerProgress, StudentKP } from '../../shared/types';

/** v2 vertical slice 鎖定的單一 KP + 科目 (橫向鋪五科時再參數化)。 */
const PRACTICE_SUBJECT = 'math' as const;
const PRACTICE_KP = 'math.div.remainder' as const;
const PRACTICE_KP_LABEL = '除法餘數';

/** 每答對一題入帳的修煉值 (純學習側 factionXP)。 */
const XP_PER_CORRECT = 10;

/** 家長批改三選一:對 / 粗心 / 不會 → Leitner outcome (§0 #1/#5)。 */
type GradeMark = 'correct' | 'careless' | 'wrong';

/**
 * 出練習卷閉環入口。`onDone` 由「✕ 離開」或批改完的「完成」按鈕觸發
 * (回 learn-portal)。
 */
export function runPracticeFlow(container: HTMLElement, onDone: () => void): void {
  container.innerHTML = '';

  const progress = loadProgress();
  if (!progress) {
    renderMissingProgress(container, onDone);
    return;
  }

  // ✕ 離開 → 回 learn-portal (中途離開不入帳,沿用既有確認語意)。
  const unmountEscape = mountEscapeButton(() => {
    unmountEscape();
    onDone();
  });

  // ── 1. 出卷 ────────────────────────────────────────────────────────────────
  // 推進卷次時鐘 (§0 #2),再以當前 Leitner 盒組卷 (§0 #3 #13)。
  advancePracticePaperCount(progress);
  const paper = composePracticePaper(progress, { subject: PRACTICE_SUBJECT, kp: PRACTICE_KP });
  // 出卷後立即存檔,讓卷次時鐘持久化 (即使中途離開也算一張卷已出)。
  saveProgress(progress);

  renderPaperView(container, paper.title, paper.questions, () => {
    unmountEscape();
    renderGradeView(container, paper.questions, progress, onDone);
  });
}

/* ─────────────────────────────────────────────────────────────────────────
   1. 考卷排版 (沿用 paper-view 版面語彙:卷頭 + 連續題號 + 作答格 + 列印)
   ───────────────────────────────────────────────────────────────────────── */

function renderPaperView(
  container: HTMLElement,
  title: string,
  questions: SmartPaperQuestion[],
  onGrade: () => void,
): void {
  container.innerHTML = '';
  ensureAcademicTheme();

  // 紙底書桌 + 白卷面(學術風格:像一張講義鋪在紙桌上)。
  const desk = makeDesk();
  const root = document.createElement('div');
  root.className = 'practice-paper exam-paper ef-paper-sheet';
  root.style.cssText = SHEET_STYLE;

  // ── 按鈕列:列印(secondary)+ 寫完了對答案(primary,主要前進動作)──────────
  const toolbar = document.createElement('div');
  toolbar.className = 'practice-paper-toolbar';
  toolbar.style.cssText = 'display:flex;gap:12px;margin:0 0 22px;flex-wrap:wrap;';

  const printBtn = academicButton({
    label: '🖨️ 列印',
    variant: 'secondary',
    onClick: () => printPaper(title, questions),
  });
  printBtn.classList.add('practice-paper-print');

  const gradeBtn = academicButton({
    label: '✅ 寫完了,對答案',
    variant: 'primary',
    onClick: onGrade,
  });
  gradeBtn.classList.add('practice-paper-grade');

  toolbar.append(printBtn, gradeBtn);
  root.appendChild(toolbar);

  // ── 卷頭 (像考卷:標題 + 班級/姓名/日期/得分) ────────────────────────────
  root.appendChild(buildHeader(title));

  // ── 題目 (連續題號 + 作答格;★挑戰題標示可跳過) ─────────────────────────
  const list = document.createElement('div');
  list.className = 'practice-paper-questions';
  questions.forEach((q, i) => list.appendChild(renderPaperQuestion(q, i + 1)));
  root.appendChild(list);

  desk.appendChild(root);
  container.appendChild(desk);
}

function buildHeader(title: string): HTMLElement {
  const header = document.createElement('div');
  header.className = 'practice-paper-header';
  header.style.cssText = 'border-bottom:2px solid var(--ef-ink);padding-bottom:12px;margin-bottom:20px;';

  const titleEl = document.createElement('h1');
  titleEl.className = 'practice-paper-title';
  titleEl.style.cssText =
    'font-family:var(--ef-serif);font-size:23px;font-weight:700;letter-spacing:1px;margin:0 0 10px;text-align:center;color:var(--ef-ink);';
  titleEl.textContent = title;
  header.appendChild(titleEl);

  const meta = document.createElement('div');
  meta.className = 'practice-paper-meta';
  meta.style.cssText =
    'display:flex;flex-wrap:wrap;gap:18px;font-size:14px;color:var(--ef-ink-soft);';
  meta.innerHTML = `
    <span>班級:${blank('3em')}</span>
    <span>姓名:${blank('5em')}</span>
    <span>日期:${blank('5em')}</span>
    <span>得分:${blank('3em')}</span>
  `;
  header.appendChild(meta);
  return header;
}

/** 單題:阿拉伯題號 + prompt + 底線作答格;★挑戰題加「寫不出來沒關係」可跳過。 */
function renderPaperQuestion(q: SmartPaperQuestion, num: number): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'practice-paper-q';
  wrap.style.cssText = 'margin:0 0 18px;';

  const promptEl = document.createElement('div');
  promptEl.className = 'practice-paper-prompt';
  promptEl.style.cssText = 'font-size:15px;margin:0 0 8px;white-space:pre-wrap;color:var(--ef-ink);';
  promptEl.textContent = `${num}. ${q.isChallenge ? '★ ' : ''}${q.prompt}`;
  wrap.appendChild(promptEl);

  if (q.isChallenge) {
    const note = document.createElement('div');
    note.className = 'practice-paper-challenge-note';
    note.style.cssText = 'font-size:13px;color:var(--ef-sub);margin:0 0 4px;';
    note.textContent = '(★ 挑戰題,寫不出來沒關係,可以跳過)';
    wrap.appendChild(note);
  }

  const line = document.createElement('div');
  line.className = 'practice-paper-underline';
  line.style.cssText = 'margin:2px 0 0;font-size:15px;color:var(--ef-ink);';
  line.innerHTML = `答:${blank('8em')}`;
  wrap.appendChild(line);

  return wrap;
}

/* ─────────────────────────────────────────────────────────────────────────
   2. 家長批改 (逐題顯示正解 + 對/粗心/不會 三鈕)
   ───────────────────────────────────────────────────────────────────────── */

function renderGradeView(
  container: HTMLElement,
  questions: SmartPaperQuestion[],
  progress: PlayerProgress,
  onDone: () => void,
): void {
  container.innerHTML = '';

  // 家長批改階段一樣可中途離開 (此時尚未入帳)。
  const unmountEscape = mountEscapeButton(() => {
    unmountEscape();
    onDone();
  });

  // 每題的家長標記 (預設未標)。★挑戰題跳過 ⇒ 預設可不標 (不計入完成)。
  const marks: (GradeMark | null)[] = questions.map(() => null);

  const finishBtn = academicButton({
    label: '完成批改',
    variant: 'primary',
    onClick: () => {
      unmountEscape();
      const summary = applyGrades(questions, marks, progress);
      renderFeedbackView(container, summary, onDone);
    },
  });
  finishBtn.classList.add('practice-grade-finish');
  finishBtn.style.marginTop = '16px';

  const desk = makeDesk();
  const root = document.createElement('div');
  root.className = 'practice-grade ef-paper-sheet';
  root.style.cssText = SHEET_STYLE;

  const heading = document.createElement('h1');
  heading.className = 'practice-grade-title';
  heading.style.cssText =
    'font-family:var(--ef-serif);font-size:21px;font-weight:700;letter-spacing:0.5px;margin:0 0 6px;color:var(--ef-ink);';
  heading.textContent = '家長批改 — 對答案';
  root.appendChild(heading);

  const hint = document.createElement('div');
  hint.className = 'practice-grade-hint';
  hint.style.cssText = 'font-size:14px;color:var(--ef-sub);margin:0 0 22px;line-height:1.6;';
  hint.textContent = '逐題看正解,幫孩子標記:【對】/【粗心】(算對但寫錯)/【不會】(觀念不懂)。';
  root.appendChild(hint);

  questions.forEach((q, i) => {
    root.appendChild(renderGradeQuestion(q, i, marks));
  });

  // ── 完成批改:每題入帳 + 即時回饋 ──────────────────────────────────────────
  root.appendChild(finishBtn);

  desk.appendChild(root);
  container.appendChild(desk);
}

/** 單題批改卡:題號 + prompt + 正解 + 三鈕 (點選後高亮)。 */
function renderGradeQuestion(
  q: SmartPaperQuestion,
  index: number,
  marks: (GradeMark | null)[],
): HTMLElement {
  const card = document.createElement('div');
  card.className = 'practice-grade-q';
  card.dataset.index = String(index);
  card.style.cssText =
    'border:1px solid var(--ef-line);border-radius:var(--ef-radius-sm);padding:14px 16px;margin:0 0 14px;background:var(--ef-paper);';

  const promptEl = document.createElement('div');
  promptEl.className = 'practice-grade-prompt';
  promptEl.style.cssText = 'font-size:15px;margin:0 0 6px;white-space:pre-wrap;color:var(--ef-ink);';
  promptEl.textContent = `${index + 1}. ${q.isChallenge ? '★ ' : ''}${q.prompt}`;
  card.appendChild(promptEl);

  const answerEl = document.createElement('div');
  answerEl.className = 'practice-grade-answer';
  // 紅筆訂正:克制的墨紅 + 左側細紅線。
  answerEl.style.cssText =
    'font-size:14px;color:#b3261e;margin:0 0 12px;border-left:3px solid #b3261e;padding-left:8px;white-space:pre-wrap;';
  const lines = [`正解:${q.correctAnswer}`];
  if (q.explanation) lines.push(`解析:${q.explanation}`);
  answerEl.textContent = lines.join('\n');
  card.appendChild(answerEl);

  const btnRow = document.createElement('div');
  btnRow.className = 'practice-grade-marks';
  btnRow.style.cssText = 'display:flex;gap:10px;flex-wrap:wrap;';

  // 對 = primary(學術綠)、粗心 = secondary(描邊)、不會 = danger(紅描邊)。
  const options: { mark: GradeMark; label: string; cls: string; variant: AcademicButtonVariant }[] = [
    { mark: 'correct', label: '對', cls: 'mark-correct', variant: 'primary' },
    { mark: 'careless', label: '粗心', cls: 'mark-careless', variant: 'secondary' },
    { mark: 'wrong', label: '不會', cls: 'mark-wrong', variant: 'danger' },
  ];

  const buttons: HTMLButtonElement[] = [];
  for (const opt of options) {
    const b = academicButton({ label: opt.label, variant: opt.variant, onClick: () => {} });
    b.classList.add('practice-grade-mark', opt.cls);
    b.dataset.mark = opt.mark;
    b.addEventListener('click', () => {
      marks[index] = opt.mark;
      // 高亮所選:選中者加 .selected(墨框圈起),其餘還原為各自 variant 樣式。
      buttons.forEach((bb) => {
        const on = bb === b;
        bb.classList.toggle('selected', on);
        bb.style.outline = on ? '2px solid var(--ef-ink)' : '';
        bb.style.outlineOffset = on ? '2px' : '';
      });
    });
    buttons.push(b);
    btnRow.appendChild(b);
  }
  card.appendChild(btnRow);

  return card;
}

/* ─────────────────────────────────────────────────────────────────────────
   3. 升降盒 + 入帳
   ───────────────────────────────────────────────────────────────────────── */

interface GradeSummary {
  /** 標「對」的題數 (修煉值入帳依此)。 */
  correctCount: number;
  /** 有被批改 (對/粗心/不會) 的題數 (未標的★跳過題不計)。 */
  gradedCount: number;
  /** 入帳的修煉值。 */
  xpGained: number;
  /** 入帳到的門派 displayName (e.g. 星算門)。 */
  factionName: string;
  /** KP Leitner 盒:批改前 → 批改後。 */
  boxBefore: number;
  boxAfter: number;
  /** 入帳後的連續登入天數。 */
  streakDays: number;
}

/**
 * 依家長標記逐題 `recordAttemptV2` 更新 Leitner (卷次驅動),再依答對數
 * `addFactionXP` 入帳 → `saveProgress`。回傳即時回饋所需的摘要 (含盒前後變化)。
 */
function applyGrades(
  questions: SmartPaperQuestion[],
  marks: (GradeMark | null)[],
  progress: PlayerProgress,
): GradeSummary {
  const kp: StudentKP = findOrCreateKP(progress, PRACTICE_KP, PRACTICE_SUBJECT);
  const boxBefore = kp.box;
  const nowISO = new Date().toISOString();

  let correctCount = 0;
  let gradedCount = 0;
  questions.forEach((q, i) => {
    const mark = marks[i];
    if (mark == null) return; // 未標 (★跳過題) 不入盒、不計分。
    gradedCount += 1;
    if (mark === 'correct') correctCount += 1;
    recordAttemptV2(progress, kp, mark, nowISO, q.prompt);
  });

  const boxAfter = kp.box;

  // 入帳:答對數 × 每題修煉值 → factionXP (合法「學習=戰力」,純學習側)。
  const faction = factionForSubject(PRACTICE_SUBJECT);
  const xpGained = correctCount * XP_PER_CORRECT;
  if (xpGained > 0) addFactionXP(progress, faction, xpGained);

  saveProgress(progress);

  return {
    correctCount,
    gradedCount,
    xpGained,
    factionName: FACTIONS[faction].displayName,
    boxBefore,
    boxAfter,
    streakDays: progress.streak?.days ?? 0,
  };
}

/* ─────────────────────────────────────────────────────────────────────────
   4. 即時回饋 (§0 #7) — 純學習側,不彈戰鬥
   ───────────────────────────────────────────────────────────────────────── */

function renderFeedbackView(
  container: HTMLElement,
  summary: GradeSummary,
  onDone: () => void,
): void {
  container.innerHTML = '';
  ensureAcademicTheme();

  const desk = makeDesk();
  const root = document.createElement('div');
  root.className = 'practice-feedback ef-paper-sheet';
  root.style.cssText =
    'font-family:var(--ef-serif);color:var(--ef-ink-soft);max-width:560px;margin:0 auto;' +
    'padding:32px 30px;background:var(--ef-paper-bright);border:1px solid var(--ef-line);' +
    'border-radius:var(--ef-radius);box-shadow:var(--ef-shadow-card);line-height:1.8;';

  const heading = document.createElement('h1');
  heading.className = 'practice-feedback-title';
  heading.style.cssText =
    'font-family:var(--ef-serif);font-size:21px;font-weight:700;letter-spacing:0.5px;margin:0 0 18px;color:var(--ef-ink);';
  heading.textContent = '批改完成';
  root.appendChild(heading);

  const list = document.createElement('div');
  list.className = 'practice-feedback-lines';
  list.style.cssText = 'font-size:16px;display:flex;flex-direction:column;gap:10px;color:var(--ef-ink);';

  const boxLine =
    summary.boxAfter === summary.boxBefore
      ? `${PRACTICE_KP_LABEL} 熟練度盒 維持 box ${summary.boxBefore}`
      : `${PRACTICE_KP_LABEL} 熟練度盒 box ${summary.boxBefore}→${summary.boxAfter}`;

  list.append(
    feedbackLine('practice-feedback-correct', `這次對 ${summary.correctCount} / ${summary.gradedCount} 題`),
    feedbackLine('practice-feedback-box', boxLine),
    feedbackLine('practice-feedback-xp', `${summary.factionName}修煉值 +${summary.xpGained}`),
    feedbackLine('practice-feedback-streak', `連續學習 ${summary.streakDays} 天`),
  );
  root.appendChild(list);

  const doneBtn = academicButton({ label: '完成', variant: 'primary', onClick: onDone });
  doneBtn.classList.add('practice-feedback-done');
  doneBtn.style.marginTop = '24px';
  root.appendChild(doneBtn);

  desk.appendChild(root);
  container.appendChild(desk);
}

function feedbackLine(cls: string, text: string): HTMLElement {
  const el = document.createElement('div');
  el.className = cls;
  el.textContent = text;
  return el;
}

/* ─────────────────────────────────────────────────────────────────────────
   列印 (沿用 paper-view 的 window.open + window.print 模式)
   ───────────────────────────────────────────────────────────────────────── */

function printPaper(title: string, questions: SmartPaperQuestion[]): void {
  const html = buildPrintableHtml(title, questions);
  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(html);
  win.document.close();
  win.focus();
  win.print();
}

/** 產生列印用 HTML 字串 (可獨立測試)。版面語彙沿用 paper-view。 */
export function buildPrintableHtml(title: string, questions: SmartPaperQuestion[]): string {
  const qs = questions
    .map((q, i) => {
      const star = q.isChallenge ? '★ ' : '';
      const note = q.isChallenge
        ? '<div class="note">(★ 挑戰題,寫不出來沒關係,可以跳過)</div>'
        : '';
      return `<div class="q">
        <div class="prompt">${i + 1}. ${star}${escapeHtml(q.prompt)}</div>
        ${note}
        <div class="answer-line">答:<span class="blank">&nbsp;</span></div>
      </div>`;
    })
    .join('');

  return `<!doctype html>
<html lang="zh-Hant">
<head>
<meta charset="utf-8">
<title>${escapeHtml(title)}</title>
<style>
  @page { size: A4 portrait; margin: 15mm; }
  body { font-family: "Songti TC", "STSong", "SimSun", "PMingLiU", serif; color: #000; line-height: 1.6; }
  .header { border-bottom: 2px solid #000; padding-bottom: 8px; margin-bottom: 16px; }
  .title { font-size: 22px; font-weight: bold; text-align: center; }
  .meta { font-size: 12px; margin-top: 8px; display: flex; flex-wrap: wrap; gap: 16px; }
  .meta .blank { display: inline-block; min-width: 4em; border-bottom: 1px solid #000; }
  .q { margin-bottom: 16px; page-break-inside: avoid; }
  .prompt { font-size: 14px; margin-bottom: 6px; white-space: pre-wrap; }
  .note { font-size: 12px; color: #555; margin-bottom: 4px; }
  .answer-line { font-size: 13px; }
  .answer-line .blank { display: inline-block; min-width: 6em; border-bottom: 1px solid #000; padding: 0 4px; }
  .print-actions { position: fixed; top: 8px; right: 8px; display: flex; gap: 8px; }
  .print-actions button { padding: 8px 16px; font-size: 14px; cursor: pointer; }
  @media print { .print-actions { display: none; } }
</style>
</head>
<body>
<div class="print-actions">
  <button onclick="window.print()">列印</button>
  <button onclick="window.close()">關閉</button>
</div>
<div class="header">
  <div class="title">${escapeHtml(title)}</div>
  <div class="meta">
    <span>班級:<span class="blank">&nbsp;</span></span>
    <span>姓名:<span class="blank">&nbsp;</span></span>
    <span>日期:<span class="blank">&nbsp;</span></span>
    <span>得分:<span class="blank">&nbsp;</span></span>
  </div>
</div>
${qs}
</body>
</html>`;
}

/* ─────────────────────────────────────────────────────────────────────────
   小 helpers
   ───────────────────────────────────────────────────────────────────────── */

function renderMissingProgress(container: HTMLElement, onDone: () => void): void {
  container.innerHTML = '';
  ensureAcademicTheme();

  const desk = makeDesk();
  const card = document.createElement('div');
  card.className = 'ef-paper-sheet';
  card.style.cssText =
    'font-family:var(--ef-serif);max-width:480px;margin:48px auto;text-align:center;' +
    'padding:32px 28px;background:var(--ef-paper-bright);border:1px solid var(--ef-line);' +
    'border-radius:var(--ef-radius);box-shadow:var(--ef-shadow-card);';

  const msg = document.createElement('div');
  msg.className = 'practice-flow-msg';
  msg.style.cssText = 'font-size:16px;line-height:1.7;color:var(--ef-ink-soft);';
  msg.textContent = '尚未建立學習進度,請先登入或建立檔案。';
  card.appendChild(msg);

  const back = academicButton({ label: '返回', variant: 'secondary', onClick: onDone });
  back.classList.add('back-btn');
  back.style.marginTop = '20px';
  card.appendChild(back);

  desk.appendChild(card);
  container.appendChild(desk);
}

function blank(width: string): string {
  return `<span class="practice-paper-blank" style="display:inline-block;min-width:${width};border-bottom:1px solid var(--ef-ink);"></span>`;
}

/**
 * 學術紙底「書桌」:頁面紙色背景(radial)+ 上下留白,讓白卷面像鋪在講義紙上。
 * 每個子畫面(出卷 / 批改 / 回饋)共用同一張桌面語彙。
 */
function makeDesk(): HTMLElement {
  const desk = document.createElement('div');
  desk.className = 'practice-desk ef-paper-desk';
  desk.style.cssText =
    'box-sizing:border-box;width:100%;min-height:100%;padding:28px 20px 48px;' +
    'background:radial-gradient(120% 80% at 50% 0%, #faf8f1 0%, var(--ef-paper) 60%);';
  return desk;
}

/**
 * 考卷紙面共用 style —— 學術紙白卷面(--ef-paper-bright)+ 墨水文字 + 細紙線框 +
 * 極淺陰影,鋪在頁面紙底上像一張真的講義。襯線、A4 寬度上限、置中作答都保留。
 */
const SHEET_STYLE =
  'font-family:var(--ef-serif);color:var(--ef-ink-soft);max-width:820px;margin:0 auto;' +
  'padding:28px 30px 64px;background:var(--ef-paper-bright);border:1px solid var(--ef-line);' +
  'border-radius:var(--ef-radius);box-shadow:var(--ef-shadow-card);line-height:1.7;';

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
